// Ctrl+V でクリップボードに画像があれば配置、テキストが URL なら OGP リンクカード化、
// それ以外は無視する。
// 配置位置は最後に追跡したマウス位置（lastPointerStagePosition）。

import { useEffect } from "react";
import { useCanvasStore } from "../store/canvas-store";

interface Handlers {
  readonly onPasteImage: (file: File, position: { x: number; y: number }) => void;
  readonly onPasteUrl: (url: string, position: { x: number; y: number }) => void;
}

export function usePasteHandler(handlers: Handlers): void {
  useEffect(() => {
    const onPaste = (event: ClipboardEvent): void => {
      // 入力要素にフォーカスがある場合は通常のペースト動作に任せる
      if (isEditableTarget(event.target)) return;

      const data = event.clipboardData;
      if (!data) return;

      const position = useCanvasStore.getState().lastPointerStagePosition ?? { x: 0, y: 0 };

      // 1) 画像があれば優先
      for (const item of Array.from(data.items)) {
        if (item.kind === "file" && item.type.startsWith("image/")) {
          const file = item.getAsFile();
          if (file) {
            event.preventDefault();
            handlers.onPasteImage(file, position);
            return;
          }
        }
      }

      // 2) テキストが URL なら OGP リンクカード化
      const text = data.getData("text/plain");
      if (text && isLikelyUrl(text)) {
        event.preventDefault();
        handlers.onPasteUrl(text.trim(), position);
      }
    };

    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [handlers]);
}

function isLikelyUrl(text: string): boolean {
  const trimmed = text.trim();
  if (trimmed.length > 2048) return false;
  return /^https?:\/\/[^\s]+$/i.test(trimmed);
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName.toLowerCase();
  return tag === "input" || tag === "textarea";
}
